import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, SquarePen } from "lucide-react";
import constant from "../../constant";

const PatientDetails = () => {
  const { patientId } = useParams();

  const navigate = useNavigate();
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hospitalId, setHospitalId] = useState(localStorage.getItem('hospitalId'));

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const options = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const options = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      // hour: '2-digit',
      // minute: '2-digit',
      // hour12: true,
    };
    return date.toLocaleString('en-US', options);
  };


  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const token = localStorage.getItem("token");
        const hospitalId = localStorage.getItem('hospitalId');

        if (!token) {
          setError("No authentication token found");
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${patientId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();

        if (response.ok) {
          setPatientData(result);
        } else {
          setError(result.message || "Failed to fetch onboard patient data");
        }
      } catch (error) {
        setError("Error fetching onboard patient data");
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [patientId]);

  const handleBack = () => {
    navigate(-1);
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800"> Patient Details</h2>
            </div>
          </div>
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>
        </div>
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800">{patientData.name}</h2>
            </div>
          </div>
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg mb-6">
            No patient data found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800">{patientData.name}</h2>
            </div>
            {/* 
            <div className="text-blue-500 cursor-pointer hover:underline">
              <Link
                to={`/onboarded/patients/${hospitalId}/edit/${patientData.patient_id}`}
                className="text-blue-600 hover:text-blue-800 font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                <SquarePen className="h-6 w-6" />
              </Link>

            </div> */}
          </div>


        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 space-y-6">
            {/* Patient Section */}
            <div className="bg-gray-50 p-6 rounded-lg space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Patient Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient Name
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient ID
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.patient_hospital_id}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.date_of_birth}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {calculateAge(patientData.date_of_birth)}

                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.gender}
                  </p>
                </div>
              </div>
            </div>

            {/* Doctor Section */}
            <div className="bg-gray-50 p-6 rounded-lg space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Doctor Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doctor Name
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.doctor.name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.doctor.mobile}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email ID
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.doctor.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Ward and Device Section */}
            <div className="bg-gray-50 p-6 rounded-lg space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Ward and Device Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.device.device_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ward Name
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.ward_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bed Number
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {patientData.bed_number}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admitted Date
                  </label>
                  <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                    {formatDate(patientData.admitted_on)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Medicine Details</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 bg-white rounded-lg">
                  <thead className="bg-brand-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Medicine Name</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Dose Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {patientData.medicine_schedules.map((schedule, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 text-sm text-gray-800 capitalize">{schedule.medicine_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-800">{formatTime(schedule.dose_time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>


          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetails;
